/**
 * Class description
 *
 * created on 2016-11-29
 * @author jowa
 */
export class RadialText extends PIXI.Container {
    private _stringSplit:string;

    get textWidth():number {
        return this._textContainer.width;
    }

    get textHeight():number {
        return this._textContainer.height;
    }


    get anchor():PIXI.ObservablePoint {
        return this._anchor;
    }

    set anchor(value:PIXI.ObservablePoint) {
        this._anchor = value;
    }

    private _debugLines:PIXI.DisplayObject[];
    private _debug:boolean = false;
    private _anchor:PIXI.ObservablePoint;
    private _normalTextContainerPosition:PIXI.Point;


    get debug():boolean {
        return this._debug;
    }

    set debug(value:boolean) {
        if(this._debug == value) {
            return;
        }

        this._debug = value;

        if(this._debug) {
            this.addDebugLines();
        }
        else {
            this.removeDebugLines();
        }
    }

    private _chars:PIXI.Text[];
    private _charContainers:PIXI.Container[];
    private _radialPivot:PIXI.Point;
    private _radius:number;
    private _arcAlignment:number;
    private _arcLength:number;
    private _totalWidth:number;
    private _letterSpacings:number[];
    private _text:string;
    private _style:PIXI.TextStyle;
    private _textWidths:number[];
    private _textContainer:PIXI.Container;

    /**
     * Text that is curved around an imaginary circle.
     * @param text The string that you would like to display
     * @param radius The radius of the imaginary circle. Small radius = more extreme arc.
     * @param arcAlignment Range from -1 to 1, The alignment of the text on the radius of the circle. -1 = outside, 1 =
     *     inside. 0 = on the line.
     * @param style Text style
     * @param arcLength Optional, Spaces the characters in the text over this many degrees. 180 = half circle. Default,
     *     normal text spacing, i.e the text length determines the arcLength.
     */
    constructor(text:string, radius:number, arcAlignment:number = 0, style:PIXI.TextStyle, arcLength?:number, stringSplit:string ="") {
        super();
        this._stringSplit = stringSplit;
        this._text = text;
        this._style = style ;
        this._radius = radius;
        this._arcAlignment = arcAlignment;
        this._radialPivot = new PIXI.Point(0, this._radius);
        this._anchor = new PIXI.ObservablePoint(() => this.onAnchorUpdate(), this, 0, 0);

        this._textContainer = new PIXI.Container();

        this.createTextParts();
        this.calculateSpacing();

        this._arcLength = arcLength ? arcLength * PIXI.DEG_TO_RAD : this._totalWidth / this._radius;

        this.addContainers();
    }

    private createTextParts() {
        this._chars = [];
        this._charContainers = [];
        this._textWidths = [];

        let pixiText:PIXI.Text;
        let container:PIXI.Container;
        this._style.letterSpacing = this._style.letterSpacing ? this._style.letterSpacing : 0;
        let letterSpacing:number = this._style.letterSpacing;
        this._totalWidth = 0;

        const subStrings = this._text.split(this._stringSplit);
        for(let i:number = 0; i < subStrings.length; i++) {

            letterSpacing = i != subStrings.length - 1 ? letterSpacing : 0;

            pixiText = new PIXI.Text(subStrings[i], this._style);
            pixiText.anchor.set(0.5, 0.5);
            pixiText.position.set(0, pixiText.height * pixiText.anchor.y * this._arcAlignment);

            container = new PIXI.Container();
            container.pivot.set(this._radialPivot.x, this._radialPivot.y);
            container.position.y = this._radialPivot.y;
            container.addChild(pixiText);

            this._chars.push(pixiText);
            this._charContainers.push(container);
            this._textWidths.push(pixiText.context.measureText(pixiText.text).width + letterSpacing);
        }
        this._totalWidth = this._chars[0].context.measureText(this._text).width + this._style.letterSpacing * this._chars.length;
    }

    private calculateSpacing() {
        this._letterSpacings = [];
        this._letterSpacings.push(this._textWidths[0] / 2);
        for(let i:number = 0; i < this._chars.length - 1; i++) {
            let first:number = this._textWidths[i] / 2;
            let second:number = this._textWidths[i + 1] / 2;
            let spacing:number = first + second;
            this._letterSpacings.push(spacing);
        }
        this._letterSpacings.push(this._textWidths[this._textWidths.length - 1] / 2);
    }

    private addContainers() {
        let radialAdvance:number = 0;
        let relativeLetterAdvance:number = 0;

        for(let i:number = 0; i < this._charContainers.length; i++) {
            let container = this._charContainers[i];
            let letterSpacing:number = this._letterSpacings[i];

            relativeLetterAdvance += letterSpacing / this._totalWidth;
            radialAdvance = relativeLetterAdvance * this._arcLength;

            container.rotation = radialAdvance - this._arcLength * 0.5;

            this._textContainer.addChild(container);
        }


        let radius:number = Math.abs(this._radius);
        let sagitta:number = radius - Math.sqrt(Math.pow(radius, 2) - Math.pow(this._totalWidth / 2, 2));
        sagitta *= this._arcAlignment;
        let yOffsetFactor:number = RadialText.map(this._arcAlignment, -1, 1, 1, 0);
        this._normalTextContainerPosition = new PIXI.Point(this._textContainer.width * 0.5, this._textContainer.height * yOffsetFactor + sagitta);

        this.placeTextContainer();


        this.addChild(this._textContainer);


    }

    /**
     * Maps value x in range inStart -> inEnd to range outStart -> outEnd.
     *
     * @param x Value to map to new range. x is a value in the in range.
     * @param inStart Start value in in range.
     * @param inEnd End value in in range.
     * @param outStart Start value in out range.
     * @param outEnd   End value in out range.
     * @returns {number}
     */
    static map(x:number, inStart:number, inEnd:number, outStart:number, outEnd:number):number {
        return (x - inStart) * (outEnd - outStart) / (inEnd - inStart) + outStart;
    }


    // ----------------------------------------------------------
    // PUBLIC
    // ----------------------------------------------------------

    /**
     * Retruns all characters in the text as a PIXI.Text[].
     *
     * @param filter string of characters that will be excluded from the returned PIXI.Text objects. Useful for
     *     excluding spaces if you want to animate only the characters.
     * @returns {PIXI.Text[]} All filtered characters.
     */
    public getCharacters(filter:string):PIXI.Text[] {
        let array:PIXI.Text[] = this._chars.concat();

        for(let i:number = 0; i < filter.length; i++) {
            array = array.filter(pixiText => pixiText.text != filter[i]);
        }

        return array;
    }


    // ----------------------------------------------------------
    // DEBUG FUNCTIONS
    // ----------------------------------------------------------

    private addDebugLines() {
        this._debugLines = [];
        for(let i:number = 0; i < this._chars.length; i++) {
            let sprite:PIXI.Text = this._chars[i];
            let container:PIXI.Container = this._charContainers[i];

            let spriteBorder = RadialText.drawSpriteBorder(sprite, 0xFF8800);
            let spriteCenter = RadialText.createCrossHair(new PIXI.Point(0, 0), 0xFF8800);
            let containerCenter = RadialText.createCrossHair(new PIXI.Point(0, 0), 0xFF0000);

            sprite.addChild(spriteBorder);
            sprite.addChild(spriteCenter);
            container.addChild(containerCenter);

            this._debugLines.push(spriteBorder);
            this._debugLines.push(spriteCenter);
            this._debugLines.push(containerCenter);
        }

        let thisPos = RadialText.createCrossHair(new PIXI.Point(0, 0), 0x00FF00);
        this.addChild(thisPos);
        this._debugLines.push(thisPos);

        let pivot = RadialText.createCrossHair(<PIXI.Point>this.pivot, 0x00FFFF);
        this.addChild(pivot);
        this._debugLines.push(pivot);

        let thisBorder = this.drawTextContainerBorder(this._textContainer, 0xFF8800);
        this.addChild(thisBorder);
        this._debugLines.push(thisBorder);


    }

    private removeDebugLines() {
        for(let i:number = 0; i < this._debugLines.length; i++) {
            let displayObject:PIXI.DisplayObject = this._debugLines[i];
            displayObject.parent.removeChild(displayObject);
        }
        this._debugLines = [];
    }

    private static createCrossHair(position:PIXI.Point, color:number):PIXI.Graphics {
        let crossHair = new PIXI.Graphics();
        crossHair.lineStyle(2, color, 1);
        crossHair.moveTo(-10, 0);
        crossHair.lineTo(10, 0);
        crossHair.moveTo(0, -10);
        crossHair.lineTo(0, 10);
        crossHair.position.set(position.x, position.y);
        return crossHair;
    }

    private drawTextContainerBorder(textContainer:PIXI.Container, color:number) {
        let border = new PIXI.Graphics();
        border.lineStyle(2, color, 1);
        border.moveTo(0, 0);
        border.lineTo(textContainer.width, 0);
        border.lineTo(textContainer.width, textContainer.height);
        border.lineTo(0, textContainer.height);
        border.lineTo(0, 0);
        border.position.set(-textContainer.width * this.anchor.x, -textContainer.height * this.anchor.y);

        return border;
    }

    private static drawSpriteBorder(sprite:PIXI.Sprite, color:number) {
        let border = new PIXI.Graphics();
        border.lineStyle(2, color, 1);
        border.moveTo(0, 0);
        border.lineTo(sprite.width, 0);
        border.lineTo(sprite.width, sprite.height);
        border.lineTo(0, sprite.height);
        border.lineTo(0, 0);
        border.position.set(-sprite.width * sprite.anchor.x, -sprite.height * sprite.anchor.y);
        return border;
    }


    private onAnchorUpdate() {
        this.placeTextContainer();
        if(this._debug == true) {
            this.removeDebugLines();
            this.addDebugLines();
        }
    }

    private placeTextContainer() {
        this._textContainer.position.set(
            this._normalTextContainerPosition.x - this._textContainer.width * this._anchor.x,
            this._normalTextContainerPosition.y - this._textContainer.height * this._anchor.y);
    }
}